import {
  PROPERTY_HERO_MEDIA_ALLOWED_TYPES,
  PROPERTY_MEDIA_ALLOWED_TYPES,
  PROPERTY_MEDIA_BUCKET,
  PROPERTY_MEDIA_MAX_BYTES,
  PROPERTY_MEDIA_PUBLIC_BASE_PATH,
} from '@/lib/constants/propertyMedia';
import { supabase } from '@/lib/supabase/client';

export interface PropertyMediaUploadResult {
  url: string;
  storagePath: string;
  fileName: string;
  fileSize: number;
  contentType: string | null;
}

interface UploadPropertyMediaParams {
  propertyId: string;
  file: File;
}

export async function removePropertyMediaFiles(storagePaths: string[]): Promise<void> {
  if (storagePaths.length === 0) {
    return;
  }

  const grouped = new Map<string, string[]>();

  for (const storagePath of storagePaths) {
    const { bucket, objectPath } = parsePropertyMediaStoragePath(storagePath);
    if (objectPath === null || objectPath.length === 0) {
      continue;
    }
    const bucketKey = bucket ?? PROPERTY_MEDIA_BUCKET;
    const existing = grouped.get(bucketKey) ?? [];
    existing.push(objectPath);
    grouped.set(bucketKey, existing);
  }

  await Promise.all(
    Array.from(grouped.entries()).map(async ([bucket, objects]) => {
      if (objects.length === 0) {
        return;
      }
      const { error } = await supabase.storage.from(bucket).remove(objects);
      if (error) {
        throw error;
      }
    }),
  );
}

function createMediaIdentifier(): string {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
  ) {
    return globalThis.crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2, 10);
}

function sanitizeFileName(fileName: string): string {
  const baseName = fileName.split('.').slice(0, -1).join('.') || fileName;
  const normalized = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 32)
    .trim();
  return normalized.length > 0 ? normalized : 'media';
}

function extractExtension(fileName: string): string {
  const parts = fileName.split('.');
  if (parts.length <= 1) {
    return '';
  }
  const ext = parts.pop();
  if (typeof ext === 'string' && ext.length > 0) {
    return `.${ext.toLowerCase()}`;
  }
  return '';
}

function buildObjectPath(propertyId: string, file: File): string {
  const identifier = createMediaIdentifier();
  const slug = sanitizeFileName(file.name);
  const extension = extractExtension(file.name);
  return `${PROPERTY_MEDIA_PUBLIC_BASE_PATH}/${propertyId}/${identifier}-${slug}${extension}`;
}

export function isAllowedPropertyMediaType(file: File): boolean {
  if (PROPERTY_MEDIA_ALLOWED_TYPES.length === 0) {
    return true;
  }
  return PROPERTY_MEDIA_ALLOWED_TYPES.includes(file.type);
}

export function isAllowedPropertyHeroMediaType(file: File): boolean {
  if (PROPERTY_HERO_MEDIA_ALLOWED_TYPES.length === 0) {
    return true;
  }
  return PROPERTY_HERO_MEDIA_ALLOWED_TYPES.includes(file.type);
}

export function isPropertyMediaVideoUrl(url: string): boolean {
  const trimmed = url.trim();
  if (trimmed.length === 0) {
    return false;
  }

  try {
    return new URL(trimmed).pathname.toLowerCase().endsWith('.mp4');
  } catch {
    return trimmed.split('?')[0]?.toLowerCase().endsWith('.mp4') ?? false;
  }
}

export function isWithinPropertyMediaSizeLimit(file: File): boolean {
  if (!PROPERTY_MEDIA_MAX_BYTES) {
    return true;
  }
  return typeof file.size === 'number' && file.size <= PROPERTY_MEDIA_MAX_BYTES;
}

export async function uploadPropertyMediaFile({
  propertyId,
  file,
}: UploadPropertyMediaParams): Promise<PropertyMediaUploadResult> {
  const safePropertyId = propertyId.trim();
  const objectPath = buildObjectPath(safePropertyId, file);
  const contentType = typeof file.type === 'string' && file.type.length > 0 ? file.type : null;

  const { error } = await supabase.storage.from(PROPERTY_MEDIA_BUCKET).upload(objectPath, file, {
    cacheControl: '3600',
    contentType: contentType ?? 'application/octet-stream',
    upsert: false,
  });

  if (error) {
    throw error;
  }

  const { data: publicUrlData } = supabase.storage
    .from(PROPERTY_MEDIA_BUCKET)
    .getPublicUrl(objectPath);
  const url = publicUrlData.publicUrl;

  return {
    storagePath: `${PROPERTY_MEDIA_BUCKET}/${objectPath}`,
    url,
    fileName: file.name,
    fileSize: typeof file.size === 'number' ? file.size : 0,
    contentType,
  };
}

export function parsePropertyMediaStoragePath(storagePath: string): {
  bucket: string | null;
  objectPath: string | null;
} {
  const trimmed = storagePath.trim().replace(/^\/+/, '');
  if (trimmed.length === 0) {
    return { bucket: null, objectPath: null };
  }
  const [bucket, ...rest] = trimmed.split('/');
  if (typeof bucket !== 'string' || bucket.length === 0) {
    return { bucket: null, objectPath: null };
  }
  const objectPath = rest.join('/');
  return { bucket, objectPath: objectPath.length > 0 ? objectPath : null };
}

export function parsePropertyMediaUrl(url: string): { bucket: string; objectPath: string } | null {
  const match = url.match(/\/storage\/v1\/object\/public\/(?<bucket>[^/]+)\/(?<objectPath>.+)$/);
  const bucket = match?.groups?.bucket;
  const objectPath = match?.groups?.objectPath;
  if (bucket == null || objectPath == null) {
    return null;
  }
  return { bucket, objectPath };
}
